import { useCallback, useState } from 'react'
import { api, messaggioErrore } from '../lib/api'
import type { VoceElevenLabs } from '../lib/tipi'

interface RispostaVoci {
  voci: VoceElevenLabs[]
  totale: number
}

/**
 * Hook per caricare e tenere in cache la lista delle voci ElevenLabs.
 *
 * Il caricamento è lazy: `caricaVoci()` va chiamato esplicitamente (di solito
 * al click del pulsante "Carica voci"). Le voci restano in stato finché
 * il componente è montato — `ricaricaVoci()` forza un refetch.
 */
export function useVociElevenLabs() {
  const [voci, setVoci] = useState<VoceElevenLabs[]>([])
  const [caricamento, setCaricamento] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [caricate, setCaricate] = useState(false)

  const caricaVoci = useCallback(async (forza = false) => {
    if (caricate && !forza) return
    setCaricamento(true)
    setErrore(null)
    try {
      const res = await api.get<{ dati: RispostaVoci }>('/superadmin/elevenlabs/voci')
      setVoci(res.data.dati?.voci ?? [])
      setCaricate(true)
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }, [caricate])

  const ricaricaVoci = useCallback(() => caricaVoci(true), [caricaVoci])

  /** Restituisce il nome della voce dato il voice_id (fallback all'id). */
  const trovaNomeVoce = useCallback(
    (voiceId: string | null | undefined): string | null => {
      if (!voiceId) return null
      const v = voci.find((x) => x.voice_id === voiceId)
      return v?.nome ?? null
    },
    [voci],
  )

  return { voci, caricamento, errore, caricate, caricaVoci, ricaricaVoci, trovaNomeVoce }
}
